<script setup>
import { ref, onMounted, computed } from "vue";
import { useSettingsStore } from "@/stores/storeSettings";
import { useRequestsStore } from "@/stores/storeRequests";
import BaseTooltip from "@/components/ui/BaseTooltip.vue";
import { useModals } from "@/composables/useModals";

import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation, Pagination, Scrollbar } from "swiper/modules";
import SliderScrollBar from "@/components/blocks/slider/SliderScrollBar.vue";
import SliderSlide from "@/components/blocks/slider/SliderSlide.vue";
import SliderSlideWeeklyPrize from "@/components/blocks/slider/SliderSlideWeeklyPrize.vue";
import SliderBtn from "@/components/blocks/slider/SliderBtn.vue";
import BaseButtonExchange from "../ui/BaseButtonExchange.vue";
import { SERVICE_OUTAGE } from "@/utils/serviceOutage";

const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const { openModal } = useModals();

const prizes = ref([]);
const grandPrize = ref([]);
const tickeWeekly = ref(null);
const tickeGrand = ref(null);

const typePrize = {
	guaranteed: "Гарантированный",
	monthly_emoji: "Ежемесячный",
	monthly_fillword: "Ежемесячный",
	weekly_anorak: "Еженедельный",
	weekly_shoper: "Еженедельный",
	weekly: "Еженедельный",
	weekly_certificate: "Еженедельный",
	weekly_ticket: "Еженедельный",
};

const getIdPeriodPrize = async (type) => {
	const [prize] = await storeRequests.getPrizes(type);
	if (!prize) return;

	const { id, title, current_period: currentPeriod } = prize;

	return { id, title, currentPeriod };
};

const formatData = (date) => {
	if (!date) return "не определенa";
	const formattedData = date.split("-").reverse();
	return `${formattedData[0]}.${formattedData[1]}.${formattedData[2]}г.`;
};

const listPrizesForTicket = computed(() => ({
	weekly: {
		id: tickeWeekly.value.id,
		title: "Покупка билета на&nbsp;участие в&nbsp;еженедельном розыгрыше",
		image: new URL("@/assets/img/prizes/gift_ticket_weekly.webp", import.meta.url).href,
		points: 500,
		description: `
			Вы покупаете билет для участия в еженедельном розыгрыше за период 
			с ${formatData(tickeWeekly.value?.currentPeriod?.start_at)} по ${formatData(tickeWeekly.value?.currentPeriod?.end_at)}<br>
			Дата розыгрыша: ${formatData(tickeWeekly.value?.currentPeriod?.draft_at)} <br>
			Разыгрываемый приз: Сертификат OZON   номиналом 1 000 рублей. 
		`,
		response: `
			Вы успешно приобрели билет для участия в еженедельном розыгрыше текущего периода.<br>
			Билет добавлен к розыгрышу и учитывается как отдельная заявка.
			Результаты будут опубликованы после проведения розыгрыша.
		`,
	},
	grand: {
		id: tickeGrand.value?.id,
		title: "Покупка билета на&nbsp;участие в&nbsp;главном розыгрыше",
		image: new URL("@/assets/img/prizes/gift_ticket_grand.webp", import.meta.url).href,
		points: 5000,
		description: `
			Вы покупаете билет для участия в розыгрыше главного приза Greenfield Клуб. <br>
			Дата розыгрыша: 22.12.2026г. <br>
			Разыгрываемый приз: 1 000 000 рублей на мечту. 

		`,
		response: `
			Вы успешно приобрели билет для участия в розыгрыше главного приза Greenfield Клуб.<br>
			Билет добавлен к розыгрышу и учитывается как отдельная заявка.
			Розыгрыш состоится 22 декабря 2026 года.
		`,
	},
}));

const swiperBrackpoints = {
	320: {
		slidesPerView: 2.1,
		spaceBetween: 18,
	},
	481: {
		slidesPerView: 2.4,
		spaceBetween: 20,
	},
	577: {
		slidesPerView: 3,
		spaceBetween: 20,
	},
	769: {
		slidesPerView: 4,
		spaceBetween: 20,
	},
	1025: {
		slidesPerView: 3.5,
		spaceBetween: 20,
	},
	1201: {
		slidesPerView: 4,
		spaceBetween: 23,
	},
};

const isActive = ref(0);
const showTab = async (id, type) => {
	isActive.value = id;
	prizes.value = await storeRequests.getPrizes(type);
};
const tabsBtns = computed(() => [
	{
		id: 0,
		className: isActive.value === 0 ? "" : "--border-white",
		text: "Еженедельные",
		type: "weekly",
	},
	{
		id: 1,
		className: isActive.value === 1 ? "" : "--border-white",
		text: "Накопительные",
		type: "point",
	},
	{
		id: 2,
		className: isActive.value === 2 ? "" : "--border-white",
		text: "за Игру «3 в ряд»",
		type: "match_3_game",
	},
]);

const isMobile = computed(() => storeSettings.widthWindow <= 480);

const info = (prize) => {
	openModal("modalPrize", { content: prize, size: "w585" });
};
const exchange = (prize) => {
	openModal("modalExchange", { content: prize, size: "w740" });
};

const sortedByPonintsPrizes = computed(() => {
	return prizes.value.sort((a, b) => a.points - b.points);
});
const sortedByFormatPrizes = computed(() => {
	return prizes.value.sort((a, b) => (b.format === "physical") - (a.format === "physical"));
});

const disabledBtnExchange = (prize) => {
	return storeRequests.userData?.points_balance < prize.points || !prize.is_available;
};

onMounted(async () => {
	prizes.value = await storeRequests.getPrizes("weekly");
	grandPrize.value = await storeRequests.getPrizes("grand");
	tickeWeekly.value = await getIdPeriodPrize("ticket_for_weekly");
	tickeGrand.value = await getIdPeriodPrize("ticket_for_grand");
});
</script>
<template>
	<section class="prizes" id="prizes">
		<div class="container">
			<div class="prizes__header">
				<h2 class="prizes__title title">Призы</h2>
				<p class="prizes__desc">
					Каждый рубль, потраченный на любой чай Greenfield, превращается в листочек. <br />
					Обменивайте их на призы или билеты для участия в еженедельном и главном розыгрышах.
				</p>
				<div class="prizes__info count-info">
					<span class="white">1 рубль</span> =
					<span class="yellow">
						1
						<svg width="24" height="24">
							<use href="#leaf"></use>
						</svg>
					</span>
				</div>
			</div>
		</div>
		<div class="container-left prizes__container">
			<div class="prizes__inner">
				<div class="grand-prize">
					<h2 class="grand-prize__title title2">Главный</h2>
					<div class="grand-prize__image">
						<img width="307" height="228" src="@/assets/img/prizes/prize_grand.webp" alt="изображение" />
					</div>
					<p class="grand-prize__desc">Для участия в розыгрыше главного приза необходимо обменять листочки на&nbsp;билеты.</p>
					<div class="grand-prize__footer">
						<div class="grand-prize__info count-info">
							<span class="white">1 билет</span> =
							<span class="yellow">
								5000
								<svg width="24" height="24">
									<use href="#leaf"></use>
								</svg>
							</span>
						</div>
						<BaseButtonExchange v-if="!storeSettings.isTelegramPage" @click="openModal('modalTicketExchange', { content: listPrizesForTicket.grand, size: 'w740' })" class="grand-prize__btn --small" title="Обменять" />
					</div>
				</div>
				<div class="prizes__list">
					<div class="prizes__tabs tabs none-scrollbar">
						<button v-for="tab in tabsBtns" :key="tab.id" @click="showTab(tab.id, tab.type)" :class="['tabs__btn btn --small', tab.className]">
							{{ tab.text }}
						</button>
					</div>
					<Transition name="slide-up" mode="out-in">
						<!-- Еженедельный приз -->
						<div v-if="isActive === 0" class="prizes__weekly weekly-prize">
							<div class="weekly-prize__header">
								<div class="weekly-prize__desc">
									<p>Для участия в розыгрыше еженедельного приза необходимо обменять листочки на билеты.</p>
									<div class="weekly-prize__info count-info">
										<span class="white">1 билет</span> =
										<span class="yellow">
											500
											<svg width="24" height="24">
												<use href="#leaf"></use>
											</svg>
										</span>
									</div>
								</div>
								<BaseButtonExchange
									v-if="!storeSettings.isTelegramPage"
									class="weekly-prize__btn --small"
									@click="
										openModal('modalTicketExchange', {
											content: listPrizesForTicket.weekly,
											size: 'w740',
										})
									"
									title="Обменять"
								/>
							</div>
							<div class="weekly-prize__slide" v-for="prize in prizes" :key="prize.id">
								<SliderSlideWeeklyPrize class="--small" :prize @info="info" />
							</div>
						</div>

						<!-- Накопительный приз -->
						<div v-else-if="isActive === 1" :class="['prizes__cumulative', { '--disabled': SERVICE_OUTAGE }]" :inert="SERVICE_OUTAGE">
							<SliderScrollBar class="prizes__scrollbar" />
							<Swiper
								:modules="[Navigation, Scrollbar]"
								:observer="true"
								:observeParents="true"
								:observeSlideChildren="true"
								:resizeObserver="true"
								:navigation="{
									nextEl: '.button-next',
									prevEl: '.button-prev',
								}"
								:scrollbar="{
									el: '.scrollbar',
									draggable: true,
								}"
								:breakpoints="swiperBrackpoints"
								class="prizes__swiper"
							>
								<SwiperSlide :class="['prizes-slide']" v-for="prize in sortedByPonintsPrizes" :key="prize.id">
									<SliderSlide :disabled="disabledBtnExchange(prize)" :isShowExchange="true" :hideExchangeBtn="storeSettings.isTelegramPage" :prize @info="info" @exchange="exchange" />
								</SwiperSlide>
							</Swiper>
							<!-- :disabled="!prize.is_available" -->
						</div>

						<!-- Игровой приз -->
						<div v-else-if="isActive === 2" class="prizes__game">
							<SliderScrollBar class="prizes__scrollbar" />
							<Swiper
								:modules="[Navigation, Scrollbar]"
								:observer="true"
								:observeParents="true"
								:observeSlideChildren="true"
								:resizeObserver="true"
								:navigation="{
									nextEl: '.button-next',
									prevEl: '.button-prev',
								}"
								:scrollbar="{
									el: '.scrollbar',
									draggable: true,
								}"
								:breakpoints="swiperBrackpoints"
								class="prizes__swiper"
							>
								<SwiperSlide :class="['prizes-slide']" v-for="prize in sortedByFormatPrizes" :key="prize.id">
									<SliderSlide :prize @info="info" />
								</SwiperSlide>
							</Swiper>
						</div>
					</Transition>
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.prizes {
	padding: 0rem 0 135px;
	@media (max-width: vars.$m) {
		padding: 0rem 0 10rem;
	}
	@media (max-width: vars.$x480) {
		padding: 0rem 0 3rem;
	}
	&__header {
		margin-bottom: 5.3rem;
		@media (max-width: vars.$x480) {
			margin-bottom: 3.2rem;
		}
	}

	&__title {
		margin-bottom: 1rem;
		@media (max-width: vars.$x480) {
			margin-bottom: 1.7rem;
		}
	}

	&__desc {
		margin-bottom: 1.5rem;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			letter-spacing: -0.2px;
			line-height: 130%;
		}
	}

	&__info {
	}

	&__container {
		@media (max-width: vars.$l) {
			padding-right: var(--padding-container);
		}
	}

	&__inner {
		display: flex;
		gap: 6.5rem;
		align-items: start;
		@media (max-width: vars.$t) {
			flex-direction: column;
			align-items: center;
		}
		@media (max-width: vars.$x480) {
			gap: 3rem;
		}
	}
	// на время сбоя накопительные призы неактивны: полупрозрачные и не реагируют на клики (inert)
	&__cumulative.--disabled {
		opacity: 0.5;
		user-select: none;
	}

	&__list {
		width: 100%;
		padding-top: 2rem;
		@media (max-width: vars.$x480) {
			padding-top: 0;
		}
	}
	&__tabs {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		margin-bottom: 3.5rem;
		@media (max-width: vars.$xx) {
			gap: 1rem;
			overflow: auto;
			padding: 0 var(--padding-container) 0.5rem 0;
			margin-right: calc(var(--padding-container) * -1);
			margin-bottom: 0.5rem;
		}
	}
	&__weekly {
		width: 83.7rem;
		@media (max-width: vars.$l) {
			width: 100%;
		}
	}
	&__cumulative {
		width: 105rem;
		@media (max-width: vars.$lg) {
			width: 100rem;
		}
		@media (max-width: vars.$l) {
			width: 77rem;
		}
		@media (max-width: vars.$t) {
			width: 100%;
		}
	}
	&__game {
		width: 105rem;
		@media (max-width: vars.$lg) {
			width: 100rem;
		}
		@media (max-width: vars.$l) {
			width: 77rem;
		}
		@media (max-width: vars.$t) {
			width: 100%;
		}
	}
	&__scrollbar {
		margin-bottom: 4rem;
		@media (max-width: vars.$x480) {
			margin-bottom: 1.5rem;
		}
	}

	&__swiper {
		@media (max-width: vars.$x480) {
			padding-right: var(--padding-container);
			margin-right: calc(var(--padding-container) * -1);
		}
	}
}
.grand-prize {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex-shrink: 0;

	position: relative;
	width: 53.9rem;
	// height: 50.8rem;
	border: 1px solid var(--border-accent-2-3);
	border-radius: 4rem;
	box-shadow: 0 15px 40px 0 rgba(0, 0, 0, 0.25);
	padding: 3.6rem 3rem 3.6rem 4rem;

	background-image: url("@/assets/img/prizes/prize_grand_bg.webp");
	background-repeat: no-repeat;
	background-position: center; /* x y*/
	background-size: cover; /* w h*/

	@media (max-width: vars.$x480) {
		width: 100%;
		border-radius: 2.4rem;
		padding: 2.5rem 2rem 3.5rem 2.5rem;
	}

	&::before {
		position: absolute;
		content: "";
		border: 1px solid var(--border-accent-2-1);
		border-radius: 5.6rem;
		width: 34.1rem;
		height: 34.1rem;
		@extend .base-bg;

		bottom: -16%;
		left: -13%;
		z-index: -1;
		@media (max-width: vars.$l) {
			bottom: -5%;
			left: -5%;
		}
		@media (max-width: vars.$x480) {
			display: none;
		}
	}
	&__title {
		margin-bottom: 2.4rem;
		color: var(--text-accent-2-100);
		@media (max-width: vars.$x480) {
			font-size: 2.8rem;
			margin-bottom: 2rem;
		}
	}

	&__image {
		align-self: center;
		margin-bottom: 2.4rem;
		margin-left: 2rem;
		@media (max-width: vars.$x480) {
			width: 16.7rem;
		}
	}

	&__desc {
		font-size: 1.6rem;
		margin-bottom: 0.5rem;
		@media (max-width: vars.$x480) {
			font-size: 1.4rem;
			line-height: 140%;
		}
	}

	&__footer {
		display: flex;
		align-items: end;
		justify-content: space-between;
	}

	&__info {
		font-size: 2rem;
		@media (max-width: vars.$x480) {
			font-size: 1.4rem;
		}
	}

	&__btn {
		@media (max-width: vars.$x480) {
			border-radius: 1.2rem;
			min-height: 3.2rem;
			font-size: 1.4rem;
		}
	}
}

.weekly-prize {
	&__header {
		position: relative;
		margin-bottom: 4rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 2rem;
		@media (max-width: vars.$x480) {
			margin-bottom: 1.5rem;
		}
	}

	&__desc {
		font-size: 1.6rem;
		line-height: 140%;
		p {
			@media (max-width: vars.$x480) {
				margin-bottom: 1rem;
			}
			@media (max-width: vars.$x360) {
				margin-bottom: 2rem;
			}
		}
	}

	&__info {
	}

	&__btn {
		@media (max-width: vars.$x480) {
			position: absolute;
			right: 0;
			bottom: 0px;

			border-radius: 1.2rem;
			min-height: 3.2rem;
			font-size: 1.4rem;
		}
		@media (max-width: vars.$x425) {
			bottom: 1.3rem;
		}
		@media (max-width: vars.$x360) {
			bottom: 1rem;
		}
	}

	&__slide {
	}
}

.prizes-slide {
	height: auto;
}

.tabs {
	&__btn {
		@media (max-width: vars.$x480) {
			min-height: 3.2rem;
			font-size: 1.4rem;
		}
	}
}

.slide-up-enter-active,
.slide-up-leave-active {
	transition: all 0.25s ease-out;
}

.slide-up-enter-from {
	opacity: 0;
	transform: translateY(30px);
}

.slide-up-leave-to {
	opacity: 0;
	transform: translateY(-30px);
}
</style>
